'use client'

import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { FormSection, FormSectionGrid } from '@/components/ui/form-section'
import { Input } from '@/components/ui/input'
import { Case, CaseType, FeeTypeEnum, LocationSchema, PersonSchema } from '../data/schema'
import { useTranslation } from 'react-i18next'
import { CasesTableParams } from '..'
import { add_case, get_case_locations, update_case, get_case_adversaries, get_adversaries, add_case_party, remove_case_party, CaseAdversary } from '@/api/case'
import { MultiInputFormField } from '@/components/multi-input-form-field'
import { get_clients } from '@/api/client'
import { PaginatedSelect } from '@/components/paginated-select'
import { ClientSelectWithAdd } from '@/components/client-select-with-add'
import { get_users } from '@/api/user'
import { SelectDropdown } from '@/components/select-dropdown'
import { handleServerError } from '@/utils/handle-server-error'
import { useCaseTypes } from '@/hooks/use-case-types'
import { useAuth } from '@/stores/authStore'
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { affectationService } from '@/api/affectation'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { X, Plus, FileText, Hash, Coins, Eye, Users } from 'lucide-react'
import { ClientsActionDialog } from '@/views/clients/components/clients-action-dialog'

interface Props {
  currentRow?: Case
  open: boolean
  onOpenChange: (open: boolean) => void
  params?: CasesTableParams
  setParams?: React.Dispatch<React.SetStateAction<CasesTableParams>>
  onUpdateSuccess?: (updatedCase: Case) => void
  prefillData?: any
}

export function CasesActionDialog({ currentRow, open, onOpenChange, params, setParams, onUpdateSuccess, prefillData }: Props) {
  const i18n = useTranslation()
  const isEdit = !!currentRow
  const { user } = useAuth()
  const { caseTypes, isLoading: caseTypesLoading } = useCaseTypes()
  // Unique per dialog instance: the add and edit instances can both be mounted
  // (close animation overlap), and two <form id='case-form'> would make the
  // footer submit button resolve to the first/empty form.
  const formId = `case-form-${currentRow?.id ?? 'new'}`

  const fetchClients = async (page: number, search?: string) => {
    const list = await get_clients({
      ordering: null,
      page: (page - 1) * 30,
      page_size: 30,
      name: search ? search : null,
    })
    const updated_list = list.results.map((v) => ({
      id: v.id,
      name: v.idnumber ? `(${v.idnumber}) ${v.name}` : v.name,
    }))
    return { items: updated_list, hasMore: list.next != null }
  }

  const fetchAdversaries = async (page: number, search?: string) => {
    const list = await get_adversaries({
      ordering: null,
      page: (page - 1) * 30,
      page_size: 30,
      search: search ? search : null,
      client_type: null,
      id_type: null,
    })
    const updated_list = list.results.map((v) => ({
      id: v.client.id,
      name: v.client.idnumber ? `(${v.client.idnumber}) ${v.client.name}` : v.client.name,
    }))
    return { items: updated_list, hasMore: list.next != null }
  }

  const fetchUsers = async (page: number, search?: string) => {
    const list = await get_users({
      roles: null,
      is_active: null,
      ordering: null,
      page: (page - 1) * 30,
      page_size: 30,
      name: search ? search : null,
    })
    return { items: list.results, hasMore: list.next != null }
  }

  const fetchCaseLocations = async (page: number, search?: string) => {
    const list = await get_case_locations({
      ordering: null,
      page: (page - 1) * 30,
      page_size: 30,
      name: search ? search : null,
    })
    return { items: list.results, hasMore: list.next != null }
  }

  const formSchema = z
    .object({
      name: z.string().min(1, { message: i18n.t('case_name_required') }),
      year: z.preprocess(
        (input) => {
          if (input === '' || input === null || input === undefined) return null
          if (typeof input === 'number') return input
          const trimmed = String(input).trim()
          if (trimmed === '') return null
          if (!/^\d+$/.test(trimmed)) return NaN
          return parseInt(trimmed, 10)
        },
        z.union([
          z.null(),
          z.number().int().positive().refine((v) => String(v).length === 4, { message: i18n.t('invalid_year') }),
        ])
      ),
      code: z.preprocess(
        (input) => {
          if (input === '' || input === null || input === undefined) return null
          if (typeof input === 'number') return input
          const trimmed = String(input).trim()
          if (trimmed === '') return null
          if (!/^\d+$/.test(trimmed)) return NaN
          return parseInt(trimmed, 10)
        },
        z.union([z.null(), z.number().int().positive({ message: i18n.t('invalid_code') })])
      ),
      number: z.preprocess(
        (input) => {
          if (input === '' || input === null || input === undefined) return null
          if (typeof input === 'number') return input
          const trimmed = String(input).trim()
          if (trimmed === '') return null
          if (!/^\d+$/.test(trimmed)) return NaN
          return parseInt(trimmed, 10)
        },
        z.union([z.null(), z.number().int().positive({ message: i18n.t('invalid_number') })])
      ),
      location: LocationSchema.nullable(),
      client: PersonSchema.nullish().refine((v) => v !== undefined && v !== null, { message: i18n.t('client_needed') }),
      assigned_to: PersonSchema.nullish()
        .refine((v) => v !== undefined && v !== null, { message: i18n.t('assigned_to_needed') })
        .nullable(),
      service_fee: z.string().nullable().refine(
        (v) => {
          if (!v) return true
          const n = Number(v)
          return !isNaN(n) && n >= 0
        },
        { message: i18n.t('invalid_fee_amount') }
      ),
      fee_type: FeeTypeEnum.nullable(),
      fee_reference_amount: z.string().nullable().refine(
        (v) => {
          if (!v) return true
          const n = Number(v)
          return !isNaN(n) && n >= 0
        },
        { message: i18n.t('invalid_fee_amount') }
      ),
      type: z.string().min(1, { message: i18n.t('type_needed') }),
      visibility: z.enum(['public', 'partial', 'private']),
    })
    .superRefine((data, ctx) => {
      const hasYear = data.year !== null
      const hasCode = data.code !== null
      const hasNumber = data.number !== null
      if ((hasYear || hasCode || hasNumber) && !(hasYear && hasCode && hasNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('invalid_full_case_id'),
          path: ['number'],
        })
      }
    })
  type CaseForm = z.infer<typeof formSchema>

  const form = useForm<CaseForm>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      year: null,
      code: null,
      number: null,
      location: null,
      client: undefined,
      assigned_to: undefined,
      service_fee: '',
      fee_type: null,
      fee_reference_amount: '',
      type: '',
      visibility: 'public',
    },
  })

  // Adversaries state management
  const [adversaries, setAdversaries] = useState<CaseAdversary[]>([])
  const [addAdversaryModalOpen, setAddAdversaryModalOpen] = useState(false)
  const [createClientModalOpen, setCreateClientModalOpen] = useState(false)
  const [selectedAdversaryTab, setSelectedAdversaryTab] = useState<'existing' | 'new'>('existing')

  const displayedAdversaries = adversaries.filter((a) => a.role === 'adversary')

  // Watch fee_type to conditionally show fee_reference_amount
  const watchedFeeType = form.watch('fee_type')

  const watchedType = form.watch('type')
  const watchedLocation = form.watch('location')
  const caseTypeId = watchedType ? parseInt(watchedType, 10) : undefined
  const locationId = watchedLocation?.id
  const villeId = watchedLocation?.ville_id ?? undefined

  const hasRecommendParams = !!(caseTypeId || locationId || villeId)

  const { data: recommendData } = useQuery({
    queryKey: ['recommend', villeId, locationId, caseTypeId],
    queryFn: () =>
      affectationService.recommend({
        ville_id: villeId,
        location_id: locationId,
        case_type_id: caseTypeId,
      }),
    enabled: open && hasRecommendParams,
    staleTime: 60_000,
  })

  const fetchLawyersForCase = async (page: number, search?: string) => {
    if (recommendData && page === 1) {
      const term = search?.toLowerCase() ?? ''
      const all = recommendData.results.filter(
        (l) => !term || l.name.toLowerCase().includes(term),
      )
      const recommended = all.filter((l) => l.is_recommended)
      const others = all.filter((l) => !l.is_recommended)
      const items = [
        ...recommended.map((l) => ({ id: l.id, name: `⭐ ${l.name}` })),
        ...others.map((l) => ({ id: l.id, name: l.name })),
      ]
      return { items, hasMore: false }
    }
    return fetchUsers(page, search)
  }

  // Populate the form ONCE, on the closed -> open transition. The ref gate is
  // deliberate: a plain effect keyed on [currentRow, user, ...] re-runs whenever
  // any of those references churn during a render and re-resets the form — which
  // is exactly what silently wiped a freshly-picked Select value mid-edit. Reset
  // belongs to "open", never to arbitrary re-renders or to close.
  const wasOpen = useRef(false)
  useEffect(() => {
    if (open && !wasOpen.current) {
      if (isEdit && currentRow) {
        form.reset({
          ...currentRow,
          location: currentRow.location || null,
          assigned_to: currentRow.assigned_to || undefined,
          service_fee: currentRow.service_fee || '',
          fee_type: currentRow.fee_type || null,
          fee_reference_amount: currentRow.fee_reference_amount || '',
          type: currentRow.type?.id.toString() || '',
          visibility: currentRow.visibility || 'public',
        })
      } else if (prefillData) {
        form.reset({
          name: prefillData.name || '',
          year: prefillData.year?.toString() || null,
          code: prefillData.code?.toString() || null,
          number: prefillData.number?.toString() || null,
          location: prefillData.location || null,
          client: prefillData.client || undefined,
          assigned_to: user ? { id: user.id, name: user.name } : undefined,
          service_fee: '',
          fee_type: null,
          fee_reference_amount: '',
          type: prefillData.type?.id?.toString() || '',
          visibility: 'public',
        })
      } else {
        form.reset({
          name: '',
          year: null,
          code: null,
          number: null,
          location: null,
          client: undefined,
          assigned_to: user ? { id: user.id, name: user.name } : undefined,
          service_fee: '',
          fee_type: null,
          fee_reference_amount: '',
          type: '',
          visibility: 'public',
        })
      }
    }
    wasOpen.current = open
  }, [open, isEdit, currentRow, prefillData, user, form])

  useEffect(() => {
    if (isEdit && currentRow && open) {
      get_case_adversaries(currentRow.id, { page: 0, page_size: 100, ordering: null, search: null, client_type: null, id_type: null })
        .then((data) => setAdversaries(data.results))
        .catch((err) => handleServerError(err))
    } else {
      setAdversaries([])
    }
  }, [isEdit, currentRow, open])

  const onSubmit = (values: CaseForm) => {
    const typeValue = values.type === '' || !values.type ? null : values.type
    const serviceFeeValue = values.service_fee === '' ? null : values.service_fee
    const feeReferenceAmountValue = values.fee_reference_amount === '' ? null : values.fee_reference_amount

    const req = {
      ...values,
      client: values.client?.id || '',
      assigned_to: values.assigned_to?.id || null,
      location: values.location?.id || null,
      type: typeValue,
      service_fee: serviceFeeValue,
      fee_type: values.fee_type,
      fee_reference_amount: values.fee_type === 'percentage' ? feeReferenceAmountValue : null,
    }

    if (isEdit && currentRow) {
      const currentTypeId = currentRow.type?.id.toString() || null
      const normalizeFee = (v: string | null | undefined) => (v ? parseFloat(v) : null)
      const noChanges =
        currentRow.name === req.name &&
        currentRow.year === req.year &&
        currentRow.code === req.code &&
        currentRow.number === req.number &&
        currentRow.location?.id === req.location &&
        currentRow.client?.id === req.client &&
        currentRow.assigned_to?.id === req.assigned_to &&
        normalizeFee(currentRow.service_fee) === normalizeFee(req.service_fee) &&
        currentRow.fee_type === req.fee_type &&
        normalizeFee(currentRow.fee_reference_amount) === normalizeFee(req.fee_reference_amount) &&
        currentTypeId === req.type &&
        currentRow.visibility === req.visibility

      if (noChanges) {
        onOpenChange(false)
        return
      }
    }

    const future = isEdit ? update_case(currentRow?.id, req) : add_case(req)

    future
      .then((apiResponse) => {
        form.reset()
        toast({ title: i18n.t(isEdit ? 'case_updated' : 'case_added', { case: values.name }) })
        onOpenChange(false)
        if (onUpdateSuccess && apiResponse) onUpdateSuccess(apiResponse as Case)
        if (!isEdit && setParams && params) setParams({ ...params })
      })
      .catch((e) => handleServerError(e))
  }

  const isAdminOrAssignee =
    user?.is_admin || (isEdit && currentRow?.assigned_to?.id === user?.id)

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size='lg' layout='stacked'>
          <DialogHeader layout='stacked' divider>
            <DialogTitle>{isEdit ? i18n.t('edit_case') : i18n.t('add_case')}</DialogTitle>
            <DialogDescription>{i18n.t('user_action')}</DialogDescription>
          </DialogHeader>

          <DialogBody>
            <Form {...form}>
              <form
                id={formId}
                onSubmit={form.handleSubmit(onSubmit, (errors) => {
                  const fieldLabels: Record<string, string> = {
                    name: i18n.t('case_name'),
                    type: i18n.t('case_type'),
                    client: i18n.t('client'),
                    assigned_to: i18n.t('assigned_to'),
                    number: i18n.t('case_number_full'),
                    year: i18n.t('case_year'),
                    code: i18n.t('case_code'),
                  }
                  const lines = Object.entries(errors)
                    .map(([key, e]: [string, any]) => e?.message ? `${fieldLabels[key] ?? key}: ${e.message}` : null)
                    .filter(Boolean)
                  toast({
                    title: i18n.t('validation_failed'),
                    description: lines.join(' · '),
                    variant: 'destructive',
                  })
                })}
                className='space-y-6'
              >
                {/* --- General info --- */}
                <FormSection
                  title={i18n.t('case_info')}
                  description={i18n.t('case_info_desc')}
                  icon={<FileText />}
                >
                  <FormSectionGrid cols={2}>
                    <FormField
                      control={form.control}
                      name='name'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {i18n.t('case_name')} <span className='text-destructive'>*</span>
                          </FormLabel>
                          <FormControl>
                            <Input placeholder={i18n.t('case_placeholder')} autoComplete='off' {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='type'
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>
                            {i18n.t('case_type')} <span className='text-destructive'>*</span>
                          </FormLabel>
                          <SelectDropdown
                            defaultValue={field.value || undefined}
                            onValueChange={field.onChange}
                            placeholder={caseTypesLoading ? i18n.t('loading') : i18n.t('select_case_type')}
                            disabled={caseTypesLoading}
                            items={caseTypes.map((t: CaseType) => ({ label: t.name, value: t.id.toString() }))}
                            isControlled={true}
                            triggerId='case-type-trigger'
                            isInvalid={!!fieldState.error}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='client'
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>
                            {i18n.t('client')} <span className='text-destructive'>*</span>
                          </FormLabel>
                          <ClientSelectWithAdd
                            defaultValue={field.value}
                            placeholder={i18n.t('select_client')}
                            fetchItems={fetchClients}
                            onSelect={field.onChange}
                            triggerId='case-client-select-trigger'
                            isInvalid={!!fieldState.error}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='location'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{i18n.t('tribunal')}</FormLabel>
                          <PaginatedSelect
                            value={field.value || undefined}
                            placeholder={i18n.t('select_case_location')}
                            fetchItems={fetchCaseLocations}
                            onSelect={field.onChange}
                            triggerId='case-tribunal-trigger'
                            className='w-full'
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='assigned_to'
                      render={({ field, fieldState }) => (
                        <FormItem className='md:col-span-2'>
                          <FormLabel>
                            {i18n.t('assigned_to')} <span className='text-destructive'>*</span>
                          </FormLabel>
                          <PaginatedSelect
                            key={`${caseTypeId ?? ''}-${locationId ?? ''}-${villeId ?? ''}-${!!recommendData}`}
                            value={field.value || undefined}
                            placeholder={i18n.t('select_user')}
                            fetchItems={fetchLawyersForCase}
                            onSelect={field.onChange}
                            triggerId='case-assigned-to-trigger'
                            isInvalid={!!fieldState.error}
                            className='w-full'
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </FormSectionGrid>
                </FormSection>

                {/* --- Case numbering --- */}
                <FormSection
                  title={i18n.t('case_number_full')}
                  icon={<Hash />}
                >
                  <MultiInputFormField
                    names={['number', 'code', 'year']}
                    label=''
                    type={['decimal', 'decimal', 'decimal']}
                    placeholders={[i18n.t('case_number'), i18n.t('case_code'), i18n.t('case_year')]}
                    layout='vertical'
                  />
                </FormSection>

                {/* --- Service fees (admin only) --- */}
                {user?.is_admin && (
                  <FormSection
                    title={i18n.t('service_fee')}
                    description={i18n.t('service_fee_desc')}
                    icon={<Coins />}
                  >
                    <FormSectionGrid cols={2}>
                      <FormField
                        control={form.control}
                        name='fee_type'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{i18n.t('fee_type')}</FormLabel>
                            <SelectDropdown
                              defaultValue={field.value || undefined}
                              onValueChange={(value) => {
                                field.onChange(value)
                                if (value === 'fixed') form.setValue('fee_reference_amount', '')
                              }}
                              placeholder={i18n.t('select_fee_type')}
                              items={[
                                { label: i18n.t('fee_type_fixed'), value: 'fixed' },
                                { label: i18n.t('fee_type_percentage'), value: 'percentage' },
                              ]}
                              isControlled={true}
                              triggerId='case-fee-type-trigger'
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name='service_fee'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {watchedFeeType === 'percentage' ? i18n.t('fee_percentage') : i18n.t('service_fee')}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={watchedFeeType === 'percentage' ? i18n.t('fee_percentage_placeholder') : i18n.t('service_fee_placeholder')}
                                autoComplete='off'
                                type='number'
                                min={0}
                                {...field}
                                value={field.value || ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {watchedFeeType === 'percentage' && (
                        <FormField
                          control={form.control}
                          name='fee_reference_amount'
                          render={({ field }) => (
                            <FormItem className='md:col-span-2'>
                              <FormLabel>{i18n.t('fee_reference_amount')}</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={i18n.t('fee_reference_amount_placeholder')}
                                  autoComplete='off'
                                  type='number'
                                  min={0}
                                  {...field}
                                  value={field.value || ''}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </FormSectionGrid>

                    {isEdit && currentRow?.calculated_fee && (
                      <div className='mt-2 flex items-center justify-between gap-4 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3'>
                        <span className='text-sm font-medium text-muted-foreground'>{i18n.t('calculated_fee')}</span>
                        <span className='text-lg font-bold text-primary'>
                          {currentRow.calculated_fee}
                          <span className='ms-1 text-xs font-normal text-muted-foreground'>{i18n.t('payment_currency')}</span>
                        </span>
                      </div>
                    )}
                  </FormSection>
                )}

                {/* --- Visibility (admin / assignee only) --- */}
                {isAdminOrAssignee && (
                  <FormSection
                    title={i18n.t('visibility')}
                    icon={<Eye />}
                  >
                    <FormField
                      control={form.control}
                      name='visibility'
                      render={({ field }) => (
                        <FormItem>
                          <SelectDropdown
                            defaultValue={field.value || undefined}
                            onValueChange={field.onChange}
                            placeholder={i18n.t('select_visibility')}
                            items={[
                              { label: i18n.t('visibility_public'), value: 'public' },
                              { label: i18n.t('visibility_partial'), value: 'partial' },
                              { label: i18n.t('visibility_private'), value: 'private' },
                            ]}
                            isControlled={true}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </FormSection>
                )}

                {/* --- Adversaries (edit mode only) --- */}
                {isEdit && currentRow && (
                  <FormSection
                    title={i18n.t('adversaries')}
                    icon={<Users />}
                    actions={
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => setAddAdversaryModalOpen(true)}
                      >
                        <Plus className='h-4 w-4 me-1' />
                        {i18n.t('add_adversary_to_case')}
                      </Button>
                    }
                  >
                    {displayedAdversaries.length > 0 ? (
                      <div className='space-y-2 max-h-56 overflow-y-auto pr-1'>
                        {displayedAdversaries.map((adversary) => (
                          <div
                            key={adversary.id}
                            className='flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-card px-3 py-2 transition-colors hover:border-border'
                          >
                            <div className='flex-1 min-w-0'>
                              <p className='text-sm font-medium text-foreground truncate'>{adversary.client.name}</p>
                              {adversary.client.idnumber && (
                                <p className='text-xs text-muted-foreground'>{adversary.client.idnumber}</p>
                              )}
                            </div>
                            <Button
                              type='button'
                              variant='ghost'
                              size='icon'
                              className='h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10'
                              onClick={() => {
                                if (confirm(i18n.t('delete_confirm', { user: adversary.client.name }))) {
                                  remove_case_party(adversary.id)
                                    .then(() => {
                                      setAdversaries(adversaries.filter((a) => a.id !== adversary.id))
                                      toast({ title: i18n.t('adversary_removed_from_case', { name: adversary.client.name }) })
                                    })
                                    .catch((err) => handleServerError(err))
                                }
                              }}
                            >
                              <X className='h-4 w-4' />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className='flex items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 py-8'>
                        <p className='text-sm text-muted-foreground'>{i18n.t('no_adversaries_in_case')}</p>
                      </div>
                    )}
                  </FormSection>
                )}
              </form>
            </Form>
          </DialogBody>

          <DialogFooter layout='stacked' divider>
            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
              {i18n.t('cancel')}
            </Button>
            <Button type='submit' form={formId}>
              {i18n.t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Adversary Modal */}
      <Dialog open={addAdversaryModalOpen} onOpenChange={setAddAdversaryModalOpen}>
        <DialogContent size='default'>
          <DialogHeader>
            <DialogTitle>{i18n.t('add_adversary_to_case')}</DialogTitle>
          </DialogHeader>

          <Tabs value={selectedAdversaryTab} onValueChange={(v) => setSelectedAdversaryTab(v as 'existing' | 'new')} className='w-full'>
            <TabsList className='grid w-full grid-cols-2'>
              <TabsTrigger value='existing'>{i18n.t('select_existing_adversary')}</TabsTrigger>
              <TabsTrigger value='new'>{i18n.t('create_new_adversary')}</TabsTrigger>
            </TabsList>

            <TabsContent value='existing' className='space-y-4'>
              <PaginatedSelect
                value={undefined}
                placeholder={i18n.t('select_client')}
                fetchItems={fetchAdversaries}
                onSelect={(client) => {
                  if (client && currentRow) {
                    add_case_party(currentRow.id, { client: String(client.id), role: 'adversary' })
                      .then(() => get_case_adversaries(currentRow.id, { page: 0, page_size: 100, ordering: null, search: null, client_type: null, id_type: null }))
                      .then((data) => {
                        setAdversaries(data.results)
                        toast({ title: i18n.t('adversary_added_to_case', { name: client.name }) })
                        setAddAdversaryModalOpen(false)
                      })
                      .catch((err) => handleServerError(err))
                  }
                }}
                triggerId='add-adversary-select-trigger'
                className='w-full'
              />
            </TabsContent>

            <TabsContent value='new'>
              <Button
                type='button'
                variant='outline'
                className='w-full'
                onClick={() => {
                  setAddAdversaryModalOpen(false)
                  setCreateClientModalOpen(true)
                }}
              >
                <Plus className='h-4 w-4 me-2' />
                {i18n.t('create_new_adversary')}
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Create Client Modal for New Adversary */}
      <ClientsActionDialog
        open={createClientModalOpen}
        onOpenChange={setCreateClientModalOpen}
        params={{ page: 0, page_size: 10, ordering: null, name: null }}
        setParams={() => {}}
        onUpdateSuccess={(newClient) => {
          if (currentRow) {
            add_case_party(currentRow.id, { client: newClient.id, role: 'adversary' })
              .then(() => get_case_adversaries(currentRow.id, { page: 0, page_size: 100, ordering: null, search: null, client_type: null, id_type: null }))
              .then((data) => {
                setAdversaries(data.results)
                toast({ title: i18n.t('adversary_added_to_case', { name: newClient.name }) })
                setCreateClientModalOpen(false)
              })
              .catch((err) => handleServerError(err))
          }
        }}
      />
    </>
  )
}
